// frontend/src/pages/TenantDetails.tsx
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// ✅ Interface adaptée aux vraies données de l'API
interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  database_name: string;
  admin_email: string;
  app_name: string;
  status: string; // 'active', 'inactive'
  deployment_status: string; // 'deployed', 'error', 'provisioning', 'pending'
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  metadata: {
    url?: string;
    database_name?: string;
    ip_address?: string;
    domain?: string;
    steps?: Record<string, string>;
    error?: string;
    deployed_at?: string;
    created_at?: string;
    [key: string]: any;
  };
}

interface HelmValues {
  tenant_id: string;
  name: string;
  subdomain: string;
  helm_values: Record<string, any>;
  deployed_at: string | null;
}

const TenantDetailsContent = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const { user, isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [checkingCert, setCheckingCert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [helmValues, setHelmValues] = useState<HelmValues | null>(null);

  const [healthCheck, setHealthCheck] = useState<{
    dns: string;
    ssl: string;
    accessibility: string;
    overall: string;
    lastChecked: string | null;
    checks?: any;
  }>({
    dns: 'unknown',
    ssl: 'unknown',
    accessibility: 'unknown',
    overall: 'unknown',
    lastChecked: null
  });

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchTenant();
  }, [isAuthenticated, isReady, tenantId, navigate]);

  // Polling intelligent basé sur l'état
  useEffect(() => {
    if (!tenant) return;

    // ✅ Si en cours de suppression, on poll toutes les 3s pour détecter quand le tenant disparaît
    if (tenant.deployment_status === 'deprovisioning' || isDeleting) {
      const interval = setInterval(async () => {
        try {
          const response = await fetch(
            apiClient.buildEndpoint(`/api/tenants/${tenantId}`)
          );
          
          // ✅ Si 404, le tenant a été supprimé (hard delete)
          if (response.status === 404) {
            clearInterval(interval);
            navigate('/admin/tenants', { 
              state: { 
                message: 'Tenant supprimé avec succès',
                type: 'success' 
              } 
            });
            return;
          }
          
          if (response.ok) {
            const data = await response.json();
            setTenant(data);
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 3000);
      
      return () => clearInterval(interval);
    }
    
    // Si en cours de provisioning -> poll fréquent (5s) avec health check
    if (tenant.deployment_status === 'provisioning') {
      const interval = setInterval(() => {
        fetchTenant();
        checkTenantHealth(); // ✅ Health check à chaque poll
      }, 5000);
      return () => clearInterval(interval);
    }
    
    // Si déployé mais certificat en provisioning -> poll toutes les 5s aussi
    if (
      tenant.deployment_status === 'deployed' &&
      (tenant.metadata.steps?.certificate === 'provisioning' || 
      healthCheck.ssl === 'provisioning' ||
      healthCheck.overall !== 'healthy')
    ) {
      const interval = setInterval(() => {
        checkTenantHealth(); // ✅ Health check toutes les 5s
      }, 5000);
      return () => clearInterval(interval);
    }
    
    // Si déployé et tout est OK (healthy) -> health check occasionnel (2 min)
    if (tenant.deployment_status === 'deployed' && healthCheck.overall === 'healthy') {
      // Check immédiat au chargement
      if (!healthCheck.lastChecked) {
        checkTenantHealth();
      }
      
      // Puis toutes les 2 minutes pour ne pas surcharger
      const interval = setInterval(checkTenantHealth, 120000);
      return () => clearInterval(interval);
    }
    
    // Si déployé mais pas encore de health check -> faire un check immédiat
    if (tenant.deployment_status === 'deployed' && !healthCheck.lastChecked) {
      checkTenantHealth();
    }
  }, [tenant?.deployment_status, tenant?.metadata.steps?.certificate, healthCheck.overall, healthCheck.ssl]);

  const loadHealthCheckFromMetadata = (tenantData: Tenant) => {
    if (tenantData.metadata?.last_health_check) {
      const hc = tenantData.metadata.last_health_check;
      setHealthCheck({
        dns: hc.dns || 'unknown',
        ssl: hc.certificate || 'unknown',
        accessibility: hc.accessibility || 'unknown',
        overall: hc.overall_health || 'unknown',
        lastChecked: hc.timestamp || null,
        checks: hc.checks
      });
    }
  };
  
  const _fetchHelmValues = async () => {
    try {
      const response = await fetch(`/api/tenants/${tenantId}/helm-values`);
      const data = await response.json();
      setHelmValues(data);
    } catch (error) {
      console.error('Error fetching helm values:', error);
    }
  };

  const fetchTenant = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/tenants/${tenantId}`)
      );
  
      // ✅ NOUVEAU : Gérer le cas du hard delete (404)
      if (response.status === 404) {
        // Si on était en train de supprimer, rediriger avec succès
        if (isDeleting) {
          navigate('/admin/tenants', { 
            state: { 
              message: 'Tenant supprimé avec succès',
              type: 'success' 
            } 
          });
          return;
        }
        // Sinon, afficher l'erreur normale
        throw new Error('Tenant introuvable ou déjà supprimé');
      }
  
      if (!response.ok) {
        throw new Error('Erreur lors du chargement du tenant');
      }
  
      const data = await response.json();
      setTenant(data);
      
      // Charger le dernier health check s'il existe
      loadHealthCheckFromMetadata(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkTenantHealth = async () => {
    if (!tenant) return;
    
    setCheckingCert(true);
    setError('');
    
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/provisioning/health-check/${tenant.id}`),
        { method: 'POST' }
      );
      
      if (response.ok) {
        const result = await response.json();
        console.log('Health check result:', result);
        
        // Mettre à jour l'état local
        setHealthCheck({
          dns: result.dns_status,
          ssl: result.certificate_status,
          accessibility: result.accessibility_status,
          overall: result.overall_health,
          lastChecked: new Date().toISOString(),
          checks: result.checks
        });
        
        // Rafraîchir les données du tenant si les métadonnées ont changé
        if (result.metadata_updated) {
          await fetchTenant();
        }
      } else {
        const errorData = await response.json();
        setError(`Erreur: ${errorData.detail || 'Impossible de vérifier l\'état'}`);
      }
    } catch (err) {
      console.error('Error checking health:', err);
      setError('Erreur lors de la vérification de l\'état');
    } finally {
      setCheckingCert(false);
    }
  };

  const handleDeprovision = async () => {
    if (!tenant) return;
    
    setDeleteLoading(true);
    setIsDeleting(true);
    setError('');

    try {
      const response = await fetch(
        apiClient.buildEndpoint('/api/provisioning/deprovision'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_id: tenant.id,
            subdomain: tenant.subdomain,
            database_name: tenant.metadata.database_name || tenant.database_name || `tenant_${tenant.subdomain.replace(/-/g, '_')}`,
            delete_database: true,
            hard_delete: true
          })
        }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      // ✅ Mettre à jour l'état local pour afficher le message de suppression
      setTenant(prev => prev ? {
        ...prev,
        deployment_status: 'deprovisioning'
      } : null);
      
      setShowDeleteModal(false);
      
      // Le polling va détecter la disparition du tenant et rediriger
    } catch (err: any) {
      setError(err.message);
      setIsDeleting(false);
      setDeleteLoading(false);
    }
  };

  const handleCheckCertificate = async () => {
    if (!tenant) return;
    
    setCheckingCert(true);
    setError(''); // Clear any previous errors
    
    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/provisioning/check-certificate/${tenant.id}`),
        { method: 'POST' }
      );
      
      if (response.ok) {
        const result = await response.json();
        console.log('Certificate check result:', result);
        
        // Rafraîchir les données du tenant
        await fetchTenant();
        
        // Optionnel : afficher un message de succès
        if (result.certificate_status === 'active') {
          // Vous pouvez ajouter un toast ou une notification ici
          console.log('✅ Certificat SSL actif');
        }
      } else {
        const errorData = await response.json();
        setError(`Erreur: ${errorData.message || 'Impossible de vérifier le certificat'}`);
      }
    } catch (err) {
      console.error('Error checking certificate:', err);
      setError('Erreur lors de la vérification du certificat');
    } finally {
      setCheckingCert(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      'provisioning': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'deployed': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'error': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      'deprovisioning': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'deleted': 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status: string) => {
    if (status === 'deployed') {
      return (
        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    if (status === 'error') {
      return (
        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
    if (status === 'provisioning' || status === 'deprovisioning') {
      return <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>;
    }
    return (
      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  };

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="!size-6" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  if (isDeleting && tenant?.deployment_status === 'deprovisioning') {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-destructive mb-4"></div>
        <h2 className="text-2xl font-bold mb-2">Suppression en cours</h2>
        <p className="text-muted-foreground text-center max-w-md">
          Le tenant <strong>{tenant.name}</strong> est en cours de suppression.
          Toutes les ressources sont en train d'être nettoyées...
        </p>
        <div className="mt-6 p-4 rounded-lg bg-muted/50 text-sm text-muted-foreground">
          <p>⏳ Cette opération peut prendre quelques minutes</p>
          <p className="mt-1">🔄 Redirection automatique une fois terminé</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="!size-8" />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6">
        <svg className="w-16 h-16 text-destructive mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-lg font-medium mb-2">{error || 'Tenant introuvable'}</p>
        <button
          onClick={() => navigate('/admin/tenants')}
          className="mt-4 px-4 py-2 rounded-md border border-input hover:bg-accent"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full overflow-auto">
      <div className="max-w-5xl mx-auto w-full p-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la liste
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">{tenant.name}</h1>
              <p className="text-muted-foreground font-mono">{tenant.subdomain}.smartlawyer.ai</p>
            </div>
            
            <div className="flex items-center gap-3">
              {tenant.deployment_status === 'deployed' && tenant.metadata.url && (
                <a
                  href={tenant.metadata.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input hover:bg-accent transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Ouvrir
                </a>
              )}

              <button
                  onClick={() => navigate(`/admin/tenants/${tenant.id}/edit`)}
                  disabled={tenant.deployment_status === 'deprovisioning'}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input hover:bg-accent disabled:opacity-50 transition-colors"
                >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Modifier
              </button>
              
              <button
                onClick={() => setShowDeleteModal(true)}
                disabled={tenant.deployment_status === 'deprovisioning'}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Supprimer
              </button>
            </div>
          </div>
        </div>

        {/* Status Card */}
        <div className="rounded-lg border bg-card p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            {getStatusIcon(tenant.deployment_status)}
            <div>
              <h2 className="text-xl font-semibold">Statut du déploiement</h2>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(tenant.deployment_status)}`}>
                {tenant.deployment_status === 'pending' && '⏳ En attente'}
                {tenant.deployment_status === 'provisioning' && '🔄 Provisioning en cours'}
                {tenant.deployment_status === 'deployed' && '✅ Déployé'}
                {tenant.deployment_status === 'error' && '❌ Erreur'}
                {tenant.deployment_status === 'deprovisioning' && '🗑️ Suppression en cours'}
                {tenant.deployment_status === 'deleted' && '🗑️ Supprimé'}
              </span>
            </div>
          </div>
          
          {/* Provisioning Steps */}
          {tenant.metadata.steps && Object.keys(tenant.metadata.steps).length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="font-medium text-sm text-muted-foreground">Étapes de provisioning:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(tenant.metadata.steps).map(([step, status]) => {
                  // ✅ Liste complète des statuts terminés (avec 'sent' ajouté)
                  const isCompleted = [
                    'completed', 'created', 'deployed', 
                    'configured', 'initialized', 'active', 'ready', 'sent'  // ← Ajout de 'sent'
                  ].includes(status);
                  
                  // ⚠️ Statuts en cours/en attente
                  
                  // ❌ Statuts d'erreur (avec 'failed' ajouté)
                  const isError = ['error', 'failed'].includes(status);
                  
                  return (
                    <div key={step} className="flex items-center gap-3 p-3 rounded-md bg-muted/30">
                      {isCompleted ? (
                        <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : isError ? (
                        <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-primary flex-shrink-0"></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {step === 'database' && '📦 Base de données'}
                          {step === 'schema' && '📋 Schéma'}
                          {step === 'admin_user' && '👤 Utilisateur admin'}
                          {step === 'static_ip' && '🌐 IP statique'}
                          {step === 'dns' && '🔗 DNS'}
                          {step === 'helm' && '⚙️ Déploiement Helm'}
                          {step === 'certificate' && '🔒 Certificat SSL'}
                          {step === 'welcome_email' && '📧 Email de bienvenue'}
                          {step === 'managers_notification' && '📧 Notification aux gérants'}
                          {![
                            'database', 'schema', 'admin_user', 'static_ip', 
                            'dns', 'helm', 'certificate', 'welcome_email', 'managers_notification'
                          ].includes(step) && step}
                        </p>
                        <p className={`text-xs ${
                          isCompleted ? 'text-green-600' : 
                          isError ? 'text-red-600' : 
                          'text-muted-foreground'
                        }`}>
                          {status === 'sent' && 'Envoyé'}
                          {status === 'failed' && 'Échec'}
                          {status === 'created' && 'Créé'}
                          {status === 'deployed' && 'Déployé'}
                          {status === 'configured' && 'Configuré'}
                          {status === 'initialized' && 'Initialisé'}
                          {status === 'active' && 'Actif'}
                          {status === 'provisioning' && 'En cours'}
                          {status === 'pending' && 'En attente'}
                          {status === 'error' && 'Erreur'}
                          {![
                            'sent', 'failed', 'created', 'deployed', 'configured', 
                            'initialized', 'active', 'provisioning', 'pending', 'error'
                          ].includes(status) && status}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {tenant.metadata.steps?.certificate === 'provisioning' && (
            <div className="mt-4 flex items-center gap-3 p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Le certificat SSL est en cours de provisioning
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Cela peut prendre quelques minutes. Le site est peut-être déjà accessible en HTTPS.
                </p>
              </div>
              <button
                onClick={handleCheckCertificate}
                disabled={checkingCert}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {checkingCert ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Vérification...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Vérifier maintenant
                  </>
                )}
              </button>
            </div>
          )}
          {/* Error Display */}
          {tenant.metadata.error && (
            <div className="mt-6 p-4 rounded-md bg-destructive/10 border border-destructive">
              <h3 className="font-medium text-sm text-destructive mb-2">Erreur:</h3>
              <pre className="text-xs text-destructive whitespace-pre-wrap font-mono overflow-x-auto">{tenant.metadata.error}</pre>
            </div>
          )}
        </div>

        {/* Health Status Card - Seulement si déployé */}
        {tenant.deployment_status === 'deployed' && (
          <div className="rounded-lg border bg-card p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">État de santé</h2>
                {healthCheck.overall === 'healthy' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    ✅ En ligne
                  </span>
                )}
                {healthCheck.overall === 'ssl_provisioning' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    🔄 SSL en cours
                  </span>
                )}
                {healthCheck.overall === 'degraded' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                    ⚠️ Dégradé
                  </span>
                )}
                {healthCheck.overall === 'dns_error' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    ❌ Erreur DNS
                  </span>
                )}
                {healthCheck.overall === 'not_accessible' && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    ❌ Inaccessible
                  </span>
                )}
              </div>
              <button
                onClick={checkTenantHealth}
                disabled={checkingCert}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-input hover:bg-accent disabled:opacity-50 transition-colors"
              >
                {checkingCert ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    Vérification...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Vérifier
                  </>
                )}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* DNS Status */}
              <div className="flex items-center gap-3 p-3 rounded-md bg-muted/30">
                {healthCheck.dns === 'resolved' ? (
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : healthCheck.dns === 'unresolved' ? (
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-gray-300 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">DNS</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {healthCheck.dns === 'resolved' ? `Résolu${healthCheck.checks?.dns?.ip_address ? ` (${healthCheck.checks.dns.ip_address})` : ''}` : 
                    healthCheck.dns === 'unresolved' ? 'Non résolu' : 
                    healthCheck.dns === 'skipped' ? 'Ignoré' :
                    'Non vérifié'}
                  </p>
                </div>
              </div>
              
              {/* SSL Status */}
              <div className="flex items-center gap-3 p-3 rounded-md bg-muted/30">
                {healthCheck.ssl === 'active' ? (
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                ) : healthCheck.ssl === 'provisioning' || healthCheck.ssl === 'timeout' ? (
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 flex-shrink-0"></div>
                ) : healthCheck.ssl === 'error' ? (
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-gray-300 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">Certificat SSL</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {healthCheck.ssl === 'active' ? `Actif${healthCheck.checks?.certificate?.issuer ? ` (${healthCheck.checks.certificate.issuer})` : ''}` : 
                    healthCheck.ssl === 'provisioning' ? 'En cours' : 
                    healthCheck.ssl === 'timeout' ? 'Timeout' :
                    healthCheck.ssl === 'error' ? 'Erreur' :
                    healthCheck.ssl === 'skipped' ? 'Ignoré' :
                    'Non vérifié'}
                  </p>
                </div>
              </div>
              
              {/* Accessibility Status */}
              <div className="flex items-center gap-3 p-3 rounded-md bg-muted/30">
                {healthCheck.accessibility === 'accessible' ? (
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : healthCheck.accessibility === 'error' || healthCheck.accessibility === 'timeout' ? (
                  <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-gray-300 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">Accessibilité</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {healthCheck.accessibility === 'accessible' ? 
                      `En ligne${healthCheck.checks?.http?.response_time_ms ? ` (${Math.round(healthCheck.checks.http.response_time_ms)}ms)` : ''}` : 
                    healthCheck.accessibility === 'timeout' ? 'Timeout' :
                    healthCheck.accessibility === 'error' ? 'Hors ligne' : 
                    healthCheck.accessibility === 'skipped' ? 'Ignoré' :
                    'Non vérifié'}
                  </p>
                </div>
              </div>
            </div>
            
            {healthCheck.lastChecked && (
              <p className="text-xs text-muted-foreground mt-4">
                Dernière vérification : {new Date(healthCheck.lastChecked).toLocaleString('fr-FR')}
              </p>
            )}
          </div>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Technical Info */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Informations techniques</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-muted-foreground">Sous-domaine</dt>
                <dd className="font-mono text-sm">{tenant.subdomain}</dd>
              </div>
              {tenant.metadata.database_name && (
                <div>
                  <dt className="text-sm text-muted-foreground">Base de données</dt>
                  <dd className="font-mono text-sm">{tenant.metadata.database_name}</dd>
                </div>
              )}
              {tenant.metadata.ip_address && (
                <div>
                  <dt className="text-sm text-muted-foreground">IP statique</dt>
                  <dd className="font-mono text-sm">{tenant.metadata.ip_address}</dd>
                </div>
              )}
              {tenant.metadata.domain && (
                <div>
                  <dt className="text-sm text-muted-foreground">Domaine</dt>
                  <dd className="font-mono text-sm">{tenant.metadata.domain}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm text-muted-foreground">Email admin</dt>
                <dd className="text-sm">{tenant.admin_email}</dd>
              </div>
            </dl>
          </div>

          {/* General Info */}
          <div className="rounded-lg border bg-card p-6">
            <h2 className="text-lg font-semibold mb-4">Informations générales</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-muted-foreground">URL</dt>
                <dd className="text-sm">
                  {tenant.metadata.url ? (
                    <a href={tenant.metadata.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {tenant.metadata.url}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">Non disponible</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-muted-foreground">Créé le</dt>
                <dd className="text-sm">{new Date(tenant.createdAt).toLocaleString('fr-FR')}</dd>
              </div>
              {tenant.metadata.deployed_at && (
                <div>
                  <dt className="text-sm text-muted-foreground">Déployé le</dt>
                  <dd className="text-sm">{new Date(tenant.metadata.deployed_at).toLocaleString('fr-FR')}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm text-muted-foreground">Statut</dt>
                <dd className="text-sm">
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                    tenant.status === 'active'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  }`}>
                    {tenant.status === 'active' ? 'Actif' : 'Inactif'}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
          {helmValues && (
            <div className="rounded-lg border bg-card p-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">⚙️ Helm Values</h3>
              <div className="text-xs font-mono bg-muted p-4 rounded overflow-auto max-h-96">
                <pre>{JSON.stringify(helmValues.helm_values, null, 2)}</pre>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-destructive/10">
                <svg className="w-6 h-6 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold">Suppression définitive</h2>
            </div>
            
            <div className="space-y-3">
              <p className="text-muted-foreground">
                Vous êtes sur le point de <strong className="text-destructive">supprimer définitivement</strong> le tenant <strong>{tenant.name}</strong>.
              </p>
              
              <div className="p-4 rounded-md bg-destructive/10 border border-destructive space-y-2">
                <p className="text-sm text-destructive font-semibold">
                  ⚠️ ATTENTION : Cette action est IRRÉVERSIBLE
                </p>
                <ul className="text-sm text-destructive space-y-1 list-disc list-inside ml-2">
                  <li>Le déploiement Kubernetes sera supprimé</li>
                  <li>L'IP statique et le DNS seront libérés</li>
                  <li>La base de données et TOUTES les données seront effacées</li>
                  <li>Le certificat SSL sera révoqué</li>
                  <li><strong>L'entrée en base sera DÉFINITIVEMENT supprimée</strong></li>
                </ul>
                <p className="text-sm text-destructive font-semibold mt-3">
                  💀 Aucune récupération ne sera possible
                </p>
              </div>
              
              <div className="p-3 rounded-md bg-muted/50 border">
                <p className="text-xs text-muted-foreground">
                  <strong>Sous-domaine :</strong> {tenant.subdomain}.smartlawyer.ai
                </p>
                <p className="text-xs text-muted-foreground">
                  <strong>Base de données :</strong> {tenant.metadata.database_name || tenant.database_name || `tenant_${tenant.subdomain.replace(/-/g, '_')}`}
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={handleDeprovision}
                disabled={deleteLoading}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {deleteLoading ? (
                  <>
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Suppression...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Oui, supprimer définitivement
                  </>
                )}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                className="px-4 py-2 rounded-md border border-input hover:bg-accent disabled:opacity-50"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function TenantDetails() {
  return (
    <Page>
      <TenantDetailsContent />
    </Page>
  );
}